import { Key, parseKey } from './key';
import { Chord, realizeRoman } from './roman';

/** A sounding note. Times are in quarter notes from the start of the piece. */
export interface NoteEvent {
  bar: number;
  onset: number;
  dur: number;
  midi: number;
  /** Spelled name, e.g. "F#4". */
  name: string;
  staff?: number;
  voice?: number;
  /** Implied contrapuntal line (0 = bass, rising), used by the voice-thread view. */
  line?: number;
  /** True when a what-if changed this note. */
  altered?: boolean;
  /** A fermata over this note (chorale phrase ends). */
  fermata?: boolean;
  /** Sung syllable; a trailing hyphen joins it to the next. */
  lyric?: string;
}

export interface Measure {
  bar: number;
  start: number;
  length: number;
}

/** One harmony label. `beat` is a quarter-note offset inside the bar. */
export interface HarmonySpec {
  bar: number;
  beat?: number;
  key: string;
  rn: string;
  /** Optional reading in a second key (a pivot chord). */
  pivot?: { key: string; rn: string };
  /** Expected non-chord tones (suspensions, held notes), as pitch classes like "C". */
  nct?: string[];
  note?: string;
}

export interface PedalSpec {
  kind: 'dominant' | 'tonic';
  from: number;
  to: number;
  pitch: string;
}

export type RegionRole = 'home' | 'away' | 'shadow' | 'return';

export interface RegionSpec {
  from: number;
  to: number;
  key: string;
  role: RegionRole;
  /** Exact span in quarters, when the key changes inside a bar. */
  start?: number;
  end?: number;
}

export type PunctuationMark = 'full' | 'semicolon' | 'comma' | 'colon' | 'ellipsis';

export interface PunctuationSpec {
  bar: number;
  /** Exact time in quarters, when it is not the start of the bar. */
  at?: number;
  mark: PunctuationMark;
  /** Short plain-language label, e.g. "arrival in G". */
  label: string;
  theory: string;
}

export interface ScriptSpec {
  from: number;
  to: number;
  path: string;
  /** Exact span in quarters, when the path does not fill whole bars. */
  start?: number;
  end?: number;
}

export interface HarmonyEvent {
  index: number;
  start: number;
  end: number;
  bar: number;
  key: Key;
  spec: HarmonySpec;
  chord: Chord;
  sounding: NoteEvent[];
  pedal?: { kind: 'dominant' | 'tonic'; barsIn: number; midi: number };
}

export interface PieceCore {
  homeKey: string;
  measures: Measure[];
  notes: NoteEvent[];
  harmony: HarmonySpec[];
  pedals: PedalSpec[];
}

export function totalLength(measures: Measure[]): number {
  const last = measures[measures.length - 1];
  return last.start + last.length;
}

export function barStart(measures: Measure[], bar: number): number {
  const m = measures.find((x) => x.bar === bar);
  if (!m) throw new Error(`No bar ${bar}`);
  return m.start;
}

export function barEnd(measures: Measure[], bar: number): number {
  const m = measures.find((x) => x.bar === bar);
  if (!m) throw new Error(`No bar ${bar}`);
  return m.start + m.length;
}

export function barAt(measures: Measure[], time: number): number {
  for (const m of measures) if (time >= m.start && time < m.start + m.length) return m.bar;
  return measures[measures.length - 1].bar;
}

/** Notes sounding at any point of [start, end). */
export function notesInSpan(notes: NoteEvent[], start: number, end: number): NoteEvent[] {
  return notes.filter((n) => n.onset < end - 1e-9 && n.onset + n.dur > start + 1e-9);
}

/** Resolve harmony labels into timed, realized events with the notes that sound under them. */
export function buildHarmonyEvents(piece: PieceCore): HarmonyEvent[] {
  const specs = [...piece.harmony].sort((a, b) => a.bar - b.bar || (a.beat ?? 0) - (b.beat ?? 0));
  const end = totalLength(piece.measures);
  const times = specs.map((s) => barStart(piece.measures, s.bar) + (s.beat ?? 0));
  return specs.map((spec, index) => {
    const start = times[index];
    const stop = index + 1 < times.length ? times[index + 1] : end;
    const key = parseKey(spec.key);
    const chord = realizeRoman(spec.rn, key);
    const sounding = notesInSpan(piece.notes, start, stop);
    let pedal: HarmonyEvent['pedal'];
    for (const p of piece.pedals) {
      if (spec.bar >= p.from && spec.bar <= p.to) {
        const low = sounding.reduce((a, n) => (n.midi < a ? n.midi : a), 999);
        pedal = { kind: p.kind, barsIn: spec.bar - p.from, midi: low };
      }
    }
    return { index, start, end: stop, bar: spec.bar, key, spec, chord, sounding, pedal };
  });
}
